'use client';

import { useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLocale } from 'next-intl';
import { templateVariantRequest, type ProductTemplateDownload } from '@/lib/product-template-downloads';
import { isBoogProduct } from '@/lib/studio/products';

interface TemplateDownloadButtonProps {
  productSlug: string;
  productName: string;
  variants?: Pick<ProductTemplateDownload, 'id' | 'label' | 'labels' | 'fileName'>[];
  /**
   * Rustige variant voor de configurator en de uploadstap: zelfde download,
   * maar als omlijnde knop in plaats van een volle blauwe balk.
   */
  compact?: boolean;
}

export function TemplateDownloadButton({ productSlug, productName, variants, compact = false }: TemplateDownloadButtonProps) {
  const locale = useLocale();
  const lang = locale === 'en' || locale === 'de' || locale === 'fr' ? locale : 'nl';
  const copy = {
    nl: { download: 'Download werkbestand', downloading: 'Downloaden...', error: 'Het werkbestand kon niet worden gedownload. Probeer het opnieuw.' },
    en: { download: 'Download artwork template', downloading: 'Downloading...', error: 'The artwork template could not be downloaded. Please try again.' },
    de: { download: 'Druckvorlage herunterladen', downloading: 'Wird heruntergeladen...', error: 'Die Druckvorlage konnte nicht heruntergeladen werden. Bitte versuchen Sie es erneut.' },
    fr: { download: 'Télécharger le gabarit PDF', downloading: 'Téléchargement...', error: 'Le gabarit n’a pas pu être téléchargé. Veuillez réessayer.' },
  }[lang];
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState('');
  const choose = { nl: 'Kies de juiste werktekeningvariant', en: 'Choose the correct template variant', de: 'Passende Vorlagenvariante auswählen', fr: 'Choisissez la variante de gabarit adaptée' }[lang];

  const handleDownload = async () => {
    const requestUrl = templateVariantRequest(productSlug, variants, selectedVariant);
    if (!requestUrl) return;
    try {
      setIsDownloading(true);

      const response = await fetch(`${requestUrl}${requestUrl.includes("?") ? "&" : "?"}locale=${lang}`);

      if (!response.ok) {
        throw new Error(copy.error);
      }

      // Create blob from response
      const blob = await response.blob();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const serverName = response.headers.get('Content-Disposition')?.match(/filename="([^"\\/]+)"/)?.[1];
      a.download = serverName || variants?.find(item => item.id === selectedVariant)?.fileName || `template-${productSlug}.pdf`;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

    } catch (error) {
      console.error('Download error:', error);
      alert(copy.error);
    } finally {
      setIsDownloading(false);
    }
  };

  if (isBoogProduct(productSlug)) {
    const labels = {
      nl: ['Multiplex 9 mm · enkelzijdig', 'Forex 10 mm · dubbelzijdig'],
      en: ['Plywood 9 mm · single-sided', 'Forex 10 mm · double-sided'],
      de: ['Sperrholz 9 mm · einseitig', 'Forex 10 mm · doppelseitig'],
      fr: ['Contreplaqué 9 mm · recto', 'Forex 10 mm · recto verso'],
    }[lang];
    if (compact) {
      return <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">{['boog-r01', 'boog-forex10-r01'].map((revision, index) => <a key={revision} href={`/studio/${revision}/template.pdf`} download={`BOOG-${revision}-ontwerpsjabloon.pdf`} className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm font-semibold text-blue-700 hover:border-blue-300 hover:bg-blue-50"><Download className="h-4 w-4 shrink-0" /><span>{copy.download}: {labels[index]}</span></a>)}</div>;
    }
    return <div className="grid gap-3 sm:grid-cols-2">{['boog-r01', 'boog-forex10-r01'].map((revision, index) => <a key={revision} href={`/studio/${revision}/template.pdf`} download={`BOOG-${revision}-ontwerpsjabloon.pdf`} className="flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-center text-sm font-semibold text-white hover:bg-blue-700"><Download className="h-5 w-5 shrink-0" /><span>{copy.download}<br />{labels[index]}</span></a>)}</div>;
  }

  const button = (
    <Button
      type="button"
      aria-label={`${copy.download}: ${productName}`}
      onClick={handleDownload}
      disabled={isDownloading || !templateVariantRequest(productSlug, variants, selectedVariant)}
      variant={compact ? 'outline' : 'default'}
      className={compact
        ? 'min-h-11 w-full border-blue-200 bg-white text-sm font-semibold text-blue-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800 sm:w-auto'
        : 'w-full bg-blue-600 hover:bg-blue-700 text-white'}
      size={compact ? 'default' : 'lg'}
    >
      {isDownloading ? (
        <>
          <Loader2 className={`${compact ? 'h-4 w-4' : 'h-5 w-5'} mr-2 animate-spin`} />
          {copy.downloading}
        </>
      ) : (
        <>
          <Download className={`${compact ? 'h-4 w-4' : 'h-5 w-5'} mr-2`} />
          {copy.download}
        </>
      )}
    </Button>
  );
  if (variants === undefined) return button;
  if (variants.length === 0) return <p role="alert" className="text-sm text-red-700">{{
    nl: 'De originele werktekeningen zijn tijdelijk niet beschikbaar. Probeer het later opnieuw of neem contact met ons op.',
    en: 'The original templates are temporarily unavailable. Please try again later or contact us.',
    de: 'Die Originalvorlagen sind vorübergehend nicht verfügbar. Bitte versuchen Sie es später erneut oder kontaktieren Sie uns.',
    fr: 'Les gabarits originaux sont temporairement indisponibles. Réessayez plus tard ou contactez-nous.',
  }[lang]}</p>;
  return <div className={compact ? 'space-y-2' : 'space-y-3'}>
    <label className={compact ? 'block text-xs font-medium text-gray-700' : 'block text-sm font-medium'}>
      {choose}
      <select aria-label={choose} className={compact ? 'mt-1 min-h-11 w-full rounded-md border border-gray-300 bg-white px-3 text-sm' : 'mt-2 w-full rounded-md border border-gray-300 bg-white p-3'} value={selectedVariant} onChange={event => setSelectedVariant(event.target.value)} disabled={isDownloading}>
        <option value="" disabled>{choose}</option>
        {variants.map(item => <option key={item.id} value={item.id}>{item.labels?.[lang] || item.label}</option>)}
      </select>
    </label>
    {button}
  </div>;
}
